import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUmbrellaBeach,
  faBed,
  faPersonWalking,
  faQuestionCircle,
  faRing,
  faBaby,
} from "@fortawesome/free-solid-svg-icons";
import {
  Box,
  Typography,
  Grid,
  Paper,
  Avatar,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Autocomplete,
  InputAdornment,
} from "@mui/material";
import {
  Search as SearchIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Close as CloseIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Badge as BadgeIcon,
  CalendarToday as CalendarIcon,
  LocationOn as LocationIcon,
  Work as WorkIcon,
  CurrencyRupee as RupeeIcon,
  AccountBalance as AccountIcon,
  BeachAccess as BeachIcon,
  LocalHospital as HospitalIcon,
  Coffee as CoffeeIcon,
  Help as HelpIcon,
  Favorite as FavoriteIcon,
  ChildCare as ChildIcon,
} from "@mui/icons-material";
import PageContainer from "../common/PageContainer";
import PageHeader from "../common/PageHeader";
import StatusChip from "../common/StatusChip";
import { format } from "date-fns";

const BASE_URL = process.env.REACT_APP_BASE_URL;

const EmployeeDetail = () => {
  const [employeeId, setEmployeeId] = useState("");
  const [employee, setEmployee] = useState(null);
  const [error, setError] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [editedEmployee, setEditedEmployee] = useState(null);

  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const token = sessionStorage.getItem("token");
        const response = await fetch(`${BASE_URL}/api/employees`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || "Error fetching employees");
        }

        const data = await response.json();
        setEmployees(data);
        setLoading(false);
      } catch (err) {
        console.error("Error fetching employees:", err);
        setError("Error fetching employees");
        setLoading(false);
      }
    };
    fetchEmployees();
  }, []);

  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearch(query);
    const normalizedQuery = query.toLowerCase().trim();
    const filtered = employees.filter((emp) => {
      const empName = (emp?.name || "").toLowerCase();
      const empId = (emp?.employeeid || "").toLowerCase();
      return (
        empName.includes(normalizedQuery) || empId.includes(normalizedQuery)
      );
    });
    setFilteredEmployees(filtered);
  };

  const handleSelectEmployee = (employeeId, employeeName) => {
    setSearch(`${employeeName} (${employeeId})`);
    setEmployeeId(employeeId);
    setFilteredEmployees([]);
  };

  const fetchEmployeeDetails = async () => {
    try {
      const searchValue = search.trim();
      const normalizedSearch = searchValue.toLowerCase();
      const matchedEmployee =
        employees.find((emp) => emp.employeeid === employeeId) ||
        employees.find(
          (emp) =>
            (emp?.employeeid || "").toLowerCase() === normalizedSearch ||
            (emp?.name || "").toLowerCase() === normalizedSearch
        );

      if (!matchedEmployee) {
        setEmployee(null);
        setEditedEmployee(null);
        setError("Employee Not Found");
        return;
      }

      const targetEmployeeId = matchedEmployee.employeeid;
      setEmployeeId(targetEmployeeId);

      const token = sessionStorage.getItem("token");
      const response = await fetch(`${BASE_URL}/api/employees/${targetEmployeeId}`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.message || "Employee not found or error occurred"
        );
      }

      const data = await response.json();
      setEmployee(data);
      setEditedEmployee(data);
      setError(null);
    } catch (err) {
      console.error("Error fetching employee details:", err);
      setEmployee(null);
      setEditedEmployee(null);
      setError("Employee Not Found");
    }
  };

  const handleEditClick = () => {
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditedEmployee(employee);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (editedEmployee?.leaveBalance && name in editedEmployee.leaveBalance) {
      setEditedEmployee((prevEmployee) => ({
        ...prevEmployee,
        leaveBalance: {
          ...prevEmployee.leaveBalance,
          [name]: value,
        },
      }));
    } else {
      setEditedEmployee((prevEmployee) => ({
        ...prevEmployee,
        [name]: value,
      }));
    }
  };

  const handleSaveChanges = async () => {
    try {
      const token = sessionStorage.getItem("token");
      const response = await fetch(`${BASE_URL}/api/employees/${employeeId}`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(editedEmployee),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Error updating employee");
      }

      const data = await response.json();
      setEmployee(data.updatedEmployee);
      setIsEditing(false);
      alert("Employee details updated successfully!");
    } catch (err) {
      console.error("Error updating employee:", err);
      alert("Error updating employee details: " + err.message);
    }
  };

  // Leave type data for rendering leave cards
  const leaveTypes = [
    {
      name: "annualLeave",
      icon: faUmbrellaBeach,
      label: "Annual Leave",
      color: "#059669",
    },
    { name: "sickLeave", icon: faBed, label: "Sick Leave", color: "#FF9800" },
    {
      name: "casualleave",
      icon: faPersonWalking,
      label: "Casual Leave",
      color: "#5C85FF",
    },
    {
      name: "Other",
      icon: faQuestionCircle,
      label: "Other Leave",
      color: "#9C27B0",
    },
    {
      name: "marriageleave",
      icon: faRing,
      label: "Marriage Leave",
      color: "#E91E63",
    },
    {
      name: "maternityleave",
      icon: faBaby,
      label: "Maternity Leave",
      color: "#FF5722",
    },
  ];

  // Label-value helper for detail view
  const InfoItem = ({ label, value }) => (
    <Box sx={{ mb: 2 }}>
      <Typography
        sx={{
          color: "#8b8fa3",
          fontSize: "0.75rem",
          fontWeight: 500,
          textTransform: "uppercase",
          letterSpacing: "0.05em",
          mb: 0.5,
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{ color: "#1a1a2e", fontSize: "0.9375rem", fontWeight: 500 }}
      >
        {value || "N/A"}
      </Typography>
    </Box>
  );

  // Section title helper
  const SectionTitle = ({ children }) => (
    <Typography
      sx={{
        fontSize: "1rem",
        fontWeight: 600,
        color: "#1a1a2e",
        borderBottom: "2px solid #f0f0f5",
        pb: 1.5,
        mb: 2,
      }}
    >
      {children}
    </Typography>
  );

  // Leave icon mapping (MUI icons replace FontAwesome)
  const leaveIconMap = {
    annualLeave: <BeachIcon />,
    sickLeave: <HospitalIcon />,
    casualleave: <CoffeeIcon />,
    Other: <HelpIcon />,
    marriageleave: <FavoriteIcon />,
    maternityleave: <ChildIcon />,
  };

  return (
    <PageContainer>
      <PageHeader
        gradient
        title="Employee Details"
        subtitle="Search and view employee information"
        icon={<PersonIcon />}
        actions={
          <Box
            component="form"
            onSubmit={(e) => {
              e.preventDefault();
              fetchEmployeeDetails();
            }}
            sx={{ display: "flex", gap: 1, alignItems: "center" }}
          >
            <Box sx={{ position: "relative", minWidth: 280 }}>
              <TextField
                size="small"
                placeholder="Search employee..."
                value={search}
                onChange={handleSearchChange}
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: "#8b8fa3", fontSize: 20 }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    bgcolor: "#fff",
                    borderRadius: "8px",
                    fontSize: "0.875rem",
                  },
                }}
              />
              {search && filteredEmployees.length > 0 && (
                <Paper
                  elevation={8}
                  sx={{
                    position: "absolute",
                    top: "100%",
                    left: 0,
                    right: 0,
                    zIndex: 10,
                    mt: 0.5,
                    maxHeight: 220,
                    overflow: "auto",
                    borderRadius: "8px",
                  }}
                >
                  {filteredEmployees.map((emp) => (
                    <Box
                      key={emp.employeeid}
                      onClick={() =>
                        handleSelectEmployee(emp.employeeid, emp.name)
                      }
                      sx={{
                        px: 2,
                        py: 1.25,
                        cursor: "pointer",
                        fontSize: "0.875rem",
                        "&:hover": { bgcolor: "#f0f2f8" },
                        borderBottom: "1px solid #f0f2f8",
                      }}
                    >
                      <Typography
                        sx={{ fontSize: "0.875rem", fontWeight: 500 }}
                      >
                        {emp.name}{" "}
                        <Typography
                          component="span"
                          sx={{ color: "#8b8fa3", fontSize: "0.8rem" }}
                        >
                          ({emp.employeeid})
                        </Typography>
                      </Typography>
                    </Box>
                  ))}
                </Paper>
              )}
              {search && filteredEmployees.length === 0 && (
                <Paper
                  elevation={8}
                  sx={{
                    position: "absolute",
                    top: "100%",
                    left: 0,
                    right: 0,
                    zIndex: 10,
                    mt: 0.5,
                    borderRadius: "8px",
                  }}
                >
                  <Box sx={{ px: 2, py: 1.5 }}>
                    <Typography sx={{ fontSize: "0.875rem", color: "#8b8fa3" }}>
                      No results found
                    </Typography>
                  </Box>
                </Paper>
              )}
            </Box>
            <Button
              type="submit"
              variant="contained"
              size="small"
              startIcon={<SearchIcon />}
              sx={{
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                textTransform: "none",
                fontWeight: 600,
                borderRadius: "8px",
                px: 2.5,
                height: 40,
                "&:hover": { background: "linear-gradient(135deg, #5a6fd8 0%, #6a3f9e 100%)" },
              }}
            >
              Search
            </Button>
          </Box>
        }
      />

      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: "8px" }}>
          {error}
        </Alert>
      )}

      {loading && !employee && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            py: 8,
            gap: 2,
          }}
        >
          <CircularProgress size={28} sx={{ color: "#667eea" }} />
          <Typography sx={{ color: "#8b8fa3" }}>
            Loading employee data...
          </Typography>
        </Box>
      )}

      {employee && (
        <>
          {/* Profile Header Card */}
          <Paper
            elevation={0}
            sx={{
              p: 3,
              mb: 3,
              borderRadius: "16px",
              border: "1px solid #eef0f6",
              bgcolor: "#fff",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: { xs: "flex-start", sm: "center" },
                flexDirection: { xs: "column", sm: "row" },
                gap: 2.5,
              }}
            >
              <Avatar
                sx={{
                  width: 80,
                  height: 80,
                  background: "linear-gradient(135deg, #667eea, #764ba2)",
                  fontSize: "1.75rem",
                  fontWeight: 700,
                }}
              >
                {(employee.name || "?").charAt(0).toUpperCase()}
              </Avatar>
              <Box sx={{ flex: 1 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    mb: 0.5,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "1.25rem",
                      fontWeight: 700,
                      color: "#1a1a2e",
                    }}
                  >
                    {employee.name}
                  </Typography>
                  <StatusChip status="active" />
                </Box>
                <Typography
                  sx={{ color: "#8b8fa3", fontSize: "0.875rem", mb: 1.5 }}
                >
                  {employee.role}
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: { xs: 2, md: 4 },
                  }}
                >
                  {[
                    {
                      icon: <EmailIcon sx={{ fontSize: 16 }} />,
                      value: employee.email,
                    },
                    {
                      icon: <PhoneIcon sx={{ fontSize: 16 }} />,
                      value: employee.contactNumber,
                    },
                    {
                      icon: <BadgeIcon sx={{ fontSize: 16 }} />,
                      value: employee.employeeid,
                    },
                    {
                      icon: <CalendarIcon sx={{ fontSize: 16 }} />,
                      value: employee.dateOfJoining
                        ? format(
                            new Date(employee.dateOfJoining),
                            "dd-MM-yyyy"
                          )
                        : "N/A",
                    },
                  ].map((item, idx) => (
                    <Box
                      key={idx}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.75,
                      }}
                    >
                      <Box sx={{ color: "#8b8fa3" }}>{item.icon}</Box>
                      <Typography
                        sx={{ color: "#3d3f52", fontSize: "0.8125rem" }}
                      >
                        {item.value || "N/A"}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
              {!isEditing && (
                <Button
                  variant="contained"
                  startIcon={<EditIcon />}
                  onClick={handleEditClick}
                  sx={{
                    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    textTransform: "none",
                    fontWeight: 600,
                    borderRadius: "8px",
                    px: 3,
                    alignSelf: { xs: "stretch", sm: "flex-start" },
                    "&:hover": { background: "linear-gradient(135deg, #5a6fd8 0%, #6a3f9e 100%)" },
                  }}
                >
                  Edit Profile
                </Button>
              )}
              {isEditing && (
                <Box
                  sx={{
                    display: "flex",
                    gap: 1,
                    alignSelf: { xs: "stretch", sm: "flex-start" },
                  }}
                >
                  <Button
                    variant="contained"
                    startIcon={<SaveIcon />}
                    onClick={handleSaveChanges}
                    sx={{
                      bgcolor: "#059669",
                      textTransform: "none",
                      fontWeight: 600,
                      borderRadius: "8px",
                      "&:hover": { bgcolor: "#047857" },
                    }}
                  >
                    Save
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<CloseIcon />}
                    onClick={handleCancelEdit}
                    sx={{
                      textTransform: "none",
                      fontWeight: 600,
                      borderRadius: "8px",
                      borderColor: "#CBD5E1",
                      color: "#8b8fa3",
                      "&:hover": {
                        borderColor: "#8b8fa3",
                        bgcolor: "#f5f7fb",
                      },
                    }}
                  >
                    Cancel
                  </Button>
                </Box>
              )}
            </Box>
          </Paper>

          {/* Detail / Edit Grid */}
          {isEditing ? (
            <Grid container spacing={3}>
              {/* Basic Information Edit */}
              <Grid item xs={12} md={6}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    borderRadius: "16px",
                    border: "1px solid #eef0f6",
                    bgcolor: "#fff",
                  }}
                >
                  <SectionTitle>Basic Information</SectionTitle>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Name"
                        name="name"
                        value={editedEmployee.name || ""}
                        onChange={handleInputChange}
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Email"
                        name="email"
                        type="email"
                        value={editedEmployee.email || ""}
                        onChange={handleInputChange}
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Contact Number"
                        name="contactNumber"
                        value={editedEmployee.contactNumber || ""}
                        onChange={handleInputChange}
                        size="small"
                      />
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>

              {/* Employment Details Edit */}
              <Grid item xs={12} md={6}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    borderRadius: "16px",
                    border: "1px solid #eef0f6",
                    bgcolor: "#fff",
                  }}
                >
                  <SectionTitle>Employment Details</SectionTitle>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Role"
                        name="role"
                        value={editedEmployee.role || ""}
                        onChange={handleInputChange}
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Manager ID"
                        name="manager"
                        value={editedEmployee.manager || ""}
                        onChange={handleInputChange}
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Manager Name"
                        name="managerName"
                        value={editedEmployee.managerName || ""}
                        onChange={handleInputChange}
                        size="small"
                      />
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>

              {/* Compensation Edit */}
              <Grid item xs={12} md={6}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    borderRadius: "16px",
                    border: "1px solid #eef0f6",
                    bgcolor: "#fff",
                  }}
                >
                  <SectionTitle>Compensation</SectionTitle>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Offered Salary"
                        name="offeredSalary"
                        type="number"
                        value={editedEmployee.offeredSalary || ""}
                        onChange={handleInputChange}
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="TDS Amount"
                        name="TDSAmount"
                        type="number"
                        value={editedEmployee.TDSAmount || ""}
                        onChange={handleInputChange}
                        size="small"
                      />
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>

              {/* Work Details Edit */}
              <Grid item xs={12} md={6}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    borderRadius: "16px",
                    border: "1px solid #eef0f6",
                    bgcolor: "#fff",
                  }}
                >
                  <SectionTitle>Work Details</SectionTitle>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Date of Joining"
                        name="dateOfJoining"
                        type="date"
                        value={editedEmployee.dateOfJoining || ""}
                        onChange={handleInputChange}
                        size="small"
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Job Location"
                        name="jobLocation"
                        value={editedEmployee.jobLocation || ""}
                        onChange={handleInputChange}
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Work Mode"
                        name="workMode"
                        value={editedEmployee.workMode || ""}
                        onChange={handleInputChange}
                        size="small"
                      />
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
            </Grid>
          ) : (
            <>
              <Grid container spacing={3}>
                {/* Personal Information */}
                <Grid item xs={12} md={6}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      borderRadius: "16px",
                      border: "1px solid #eef0f6",
                      bgcolor: "#fff",
                      height: "100%",
                    }}
                  >
                    <SectionTitle>Personal Information</SectionTitle>
                    <Grid container spacing={0}>
                      <Grid item xs={6}>
                        <InfoItem label="Email" value={employee.email} />
                      </Grid>
                      <Grid item xs={6}>
                        <InfoItem
                          label="Contact"
                          value={employee.contactNumber}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <InfoItem
                          label="Manager"
                          value={
                            employee.managerName
                              ? `${employee.managerName} (${employee.manager})`
                              : employee.manager
                          }
                        />
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>

                {/* Professional Information */}
                <Grid item xs={12} md={6}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      borderRadius: "16px",
                      border: "1px solid #eef0f6",
                      bgcolor: "#fff",
                      height: "100%",
                    }}
                  >
                    <SectionTitle>Professional Information</SectionTitle>
                    <Grid container spacing={0}>
                      <Grid item xs={6}>
                        <InfoItem
                          label="Date of Joining"
                          value={
                            employee.dateOfJoining
                              ? format(
                                  new Date(employee.dateOfJoining),
                                  "dd-MM-yyyy"
                                )
                              : "N/A"
                          }
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <InfoItem
                          label="Job Location"
                          value={employee.jobLocation}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <InfoItem
                          label="Work Mode"
                          value={employee.workMode}
                        />
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>

                {/* Compensation */}
                <Grid item xs={12} md={6}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      borderRadius: "16px",
                      border: "1px solid #eef0f6",
                      bgcolor: "#fff",
                    }}
                  >
                    <SectionTitle>Compensation</SectionTitle>
                    <Grid container spacing={0}>
                      <Grid item xs={6}>
                        <InfoItem
                          label="Offered Salary"
                          value={
                            employee.offeredSalary
                              ? `₹${employee.offeredSalary}`
                              : "N/A"
                          }
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <InfoItem
                          label="TDS Amount"
                          value={
                            employee.TDSAmount
                              ? `₹${employee.TDSAmount}`
                              : "N/A"
                          }
                        />
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>

                {/* Leave Balances */}
                <Grid item xs={12}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      borderRadius: "16px",
                      border: "1px solid #eef0f6",
                      bgcolor: "#fff",
                    }}
                  >
                    <SectionTitle>Leave Balances</SectionTitle>
                    <Grid container spacing={2}>
                      {leaveTypes.map((leave) => (
                        <Grid item xs={12} sm={6} md={4} key={leave.name}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 2,
                              p: 2,
                              borderRadius: "10px",
                              bgcolor: "#FAFBFC",
                              borderLeft: `4px solid ${leave.color}`,
                            }}
                          >
                            <Box
                              sx={{
                                width: 40,
                                height: 40,
                                borderRadius: "8px",
                                bgcolor: `${leave.color}15`,
                                color: leave.color,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                flexShrink: 0,
                              }}
                            >
                              {leaveIconMap[leave.name] || <HelpIcon />}
                            </Box>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography
                                sx={{
                                  fontSize: "0.8125rem",
                                  fontWeight: 600,
                                  color: "#1a1a2e",
                                  mb: 0.25,
                                }}
                              >
                                {leave.label}
                              </Typography>
                              <Box
                                sx={{
                                  display: "flex",
                                  gap: 2,
                                }}
                              >
                                <Typography
                                  sx={{
                                    fontSize: "0.75rem",
                                    color: "#8b8fa3",
                                  }}
                                >
                                  Total:{" "}
                                  <Box
                                    component="span"
                                    sx={{
                                      fontWeight: 600,
                                      color: "#3d3f52",
                                    }}
                                  >
                                    {employee.leaveBalance?.[leave.name] ||
                                      employee[leave.name] ||
                                      0}
                                  </Box>
                                </Typography>
                                <Typography
                                  sx={{
                                    fontSize: "0.75rem",
                                    color: "#8b8fa3",
                                  }}
                                >
                                  Remaining:{" "}
                                  <Box
                                    component="span"
                                    sx={{
                                      fontWeight: 600,
                                      color: leave.color,
                                    }}
                                  >
                                    {employee.remainingLeave?.[leave.name] ||
                                      employee[leave.name] ||
                                      0}
                                  </Box>
                                </Typography>
                              </Box>
                            </Box>
                          </Box>
                        </Grid>
                      ))}
                    </Grid>
                  </Paper>
                </Grid>
              </Grid>
            </>
          )}
        </>
      )}
    </PageContainer>
  );
};

export default EmployeeDetail;
